import { css, html, LitElement, nothing, type TemplateResult } from 'lit';
import '../editors/elements/d-checkbox.js';
import { customElement, property } from 'lit/decorators.js';
import '../elements/d-smooth-resize.js';
import type { ActivityCodeCommon } from 'src/store/selectors';
import _ from 'lodash';

/**
 *
 * STATUS OK
 */
@customElement('d-common-activity-codes')
export class DCommonActivityCodes extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
    }
    d-checkbox {
      margin-bottom: 4px;
    }
    div {
      padding-left: 30px;
    }
  `;
  @property({ type: Array })
  activityCodes: string[] = [];
  @property({ type: Array })
  commonActivityCodes: ActivityCodeCommon[] = [];
  @property({ type: Boolean, attribute: 'theme-page' })
  themePage = false;
  @property({ type: Boolean, reflect: true })
  outskirts = false;

  private selected(activityCodes: string[], codes: string[]): boolean {
    if (activityCodes !== undefined && codes !== undefined) {
      for (const item of codes) {
        if (!activityCodes.includes(item)) {
          return false;
        }
      }
    }
    return true;
  }

  flatActiveCommonCodes() {
    const result: ActivityCodeCommon[] = [];
    this.commonActivityCodes.forEach((l1) => {
      if (this.selected(this.activityCodes, l1.code)) {
        result.push(l1);
        (l1.codes ?? []).forEach((l2) => {
          if (this.selected(this.activityCodes, l2.code)) {
            result.push(l2);
            (l2.codes ?? []).forEach((l3) => {
              if (this.selected(this.activityCodes, l3.code)) {
                result.push(l3);
              }
            });
          }
        });
      }
    });
    return result;
  }

  renderActivityCodes(codes: ActivityCodeCommon[] | undefined): TemplateResult<1> {
    return html`
      ${(codes ?? []).map(
        (code) => html`
          <d-checkbox
            @checked-changed=${(e) => this.onCheckedChanged(e, code.code)}
            ?checked=${this.selected(this.activityCodes, code.code)}
            .optionLabel=${code.name}
            ?theme-page=${this.themePage}
            ?outskirts=${this.outskirts}
          ></d-checkbox>
          <d-smooth-resize>
            ${this.selected(this.activityCodes, code.code)
              ? html` <div>${this.renderActivityCodes(code.codes)}</div>`
              : nothing}
          </d-smooth-resize>
        `,
      )}
    `;
  }

  render(): TemplateResult<1> {
    return this.renderActivityCodes(this.commonActivityCodes);
  }

  private removeItemAndChildren(f: ActivityCodeCommon[], i: ActivityCodeCommon) {
    let list = [...f];
    list = list.filter((x) => !_.isEqual(x.code, i.code));
    for (const childItem of i.codes ?? []) {
      list = this.removeItemAndChildren(list, childItem);
    }
    return list;
  }

  private removeCodes(codes: string[]) {
    const f = this.flatActiveCommonCodes();
    const itemToRemove = f.find((x) => _.isEqual(x.code, codes));
    if (itemToRemove === undefined) {
      throw new Error('Illegal state (E071), item to remove not found for codes ' + JSON.stringify(codes));
    }
    const n = this.removeItemAndChildren(f, itemToRemove);

    return _.uniq(n.flatMap((x) => x.code));
  }
  private addCodes(codes: string[]) {
    const targetCodes = codes.filter((item) => !this.activityCodes.includes(item));
    return [...this.activityCodes, ...targetCodes];
  }

  private onCheckedChanged(e: CustomEvent<{ checked: boolean }>, codes: string[]) {
    e.preventDefault();
    e.stopPropagation();
    this.activityCodes = e.detail.checked ? this.addCodes(codes) : this.removeCodes(codes);
    this.dispatchEvent(
      new CustomEvent<{ codes: string[] }>('activity-codes-changed', {
        bubbles: true,
        composed: true,
        detail: {
          codes: this.activityCodes,
        },
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-common-activity-codes': DCommonActivityCodes;
  }
}
